import React, {useState, useEffect, useLayoutEffect, useMemo, useCallback, forwardRef, useRef, useContext} from "react"
import Select, {components as selectComponents, mergeStyles} from "react-select"
import AsyncSelect from "react-select/async"
import CreateableSelect from "react-select/creatable"
import AsyncCreateableSelect from "react-select/async-creatable"
import {get, isString, uniqueId} from "../utilities/object"

import {globalProps, GlobalProps} from "../utilities/globalProps"
import classnames from "classnames"

import {
  Control,
  ClearIndicator,
  IndicatorsContainer,
  MenuList,
  MultiValue,
  Option,
  Placeholder,
  ValueContainer,
} from "./components"

import * as kitComponents from "./components"

import {noop, buildDataProps, buildHtmlProps} from "../utilities/props"
import {
  PB_FLOATING_OWNER_ATTR,
  kitRequiresPortaledFloatingUi,
  resolveFloatingOwnerId,
  resolvePortaledFloatingZIndex,
  resolvePortaledKitHost,
} from "../utilities/floatingPortalHosts"
import {DialogContext} from "../pb_dialog/_dialog_context"
import {GenericObject, Noop} from "../types"

type TypeaheadStylesConfig = Parameters<typeof mergeStyles>[0]

/**
 * @typedef {object} Props
 * @prop {boolean} async - whether Typeahead should fetch data from
 * a remote location to populate the options
 * @prop {string} label - the text for the optional typeahead input label
 * @prop {boolean} preserveSearchInput - whether to preserve the input value when the field loses focus
 */

type TypeaheadProps = {
  async?: boolean
  className?: string
  components?: GenericObject
  createable?: boolean
  dark?: boolean
  data?: {[key: string]: string}
  disabled?: boolean
  error?: string
  htmlOptions?: {[key: string]: string | number | boolean | (() => void)}
  id?: string
  inputDisplay?: "pills" | "none"
  label?: string
  loadOptions?: string | Noop
  getOptionLabel?: string | (() => string)
  getOptionValue?: string | (() => string)
  name?: string
  options?: Array<{label: string; value?: string}>
  marginBottom?: "none" | "xxs" | "xs" | "sm" | "md" | "lg" | "xl"
  pillColor?:
    | "primary"
    | "neutral"
    | "success"
    | "warning"
    | "error"
    | "info"
    | "data_1"
    | "data_2"
    | "data_3"
    | "data_4"
    | "data_5"
    | "data_6"
    | "data_7"
    | "data_8"
    | "windows"
    | "siding"
    | "roofing"
    | "doors"
    | "gutters"
    | "solar"
    | "insulation"
    | "accessories"
  onChange?: any
  optionsByContext?: Record<string, Array<{label: string; value?: string}>>
  required?: boolean
  requiredIndicator?: boolean
  validation?: {message: string}
  searchContextSelector?: string
  clearOnContextChange?: boolean
  preserveSearchInput?: boolean
  enablePillReorder?: boolean
  externalDragHandle?: boolean
  showPillIndex?: boolean
  // Passed through to react-select; merged when menu is portaled to `document.body`.
  styles?: TypeaheadStylesConfig
} & GlobalProps

export type SelectValueType = {
  label?: string,
  value: string,
  name?: string,
  imageUrl?: string,
  pillColor?: string,
}

type TagOnChangeValues = {
  action?: string
  option?: SelectValueType
  removedValue?: string
}

/**
 * @constant {React.ReactComponent} Typeahead
 * @param {TypeaheadProps} props - props as described at https://react-select.com/props
 */
const Typeahead = forwardRef<HTMLInputElement, TypeaheadProps>(
  ({
    async,
    className,
    components = {},
    createable,
    error = "",
    data = {},
    disabled = false,
    getOptionLabel,
    getOptionValue,
    htmlOptions = {},
    id,
    inputDisplay = "pills",
    name,
    label,
    loadOptions = noop,
    marginBottom = "sm",
    pillColor,
    onChange,
    optionsByContext = {},
    searchContextSelector,
    required = false,
    requiredIndicator = false,
    validation,
    clearOnContextChange = false,
    preserveSearchInput = false, // Default to false to maintain backward compatibility
    enablePillReorder = false,
    externalDragHandle = false,
    showPillIndex = false,
    styles: stylesProp,
    ...props
  }: TypeaheadProps) => {
    // State to manage the input value when preserveSearchInput is true
    const [inputValue, setInputValue] = useState("")
    // State to track if form has been submitted to control validation display for react rendered rails kit
    const [formSubmitted, setFormSubmitted] = useState(false)
    // State to track if user has made a selection (to disable defaultValue focus behavior)
    const [hasUserSelected, setHasUserSelected] = useState(false)
    // Async loading state (used for aria-busy + optional external guards)
    const [asyncLoading, setAsyncLoading] = useState(false)
    const mountedRef = useRef(true)

    // Watchdog + sequence to avoid stuck loading and stale request completion
    const asyncLoadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    )
    const asyncRequestSeqRef = useRef(0)

    const clearAsyncLoadingTimeout = () => {
      if (asyncLoadingTimeoutRef.current) {
        clearTimeout(asyncLoadingTimeoutRef.current)
        asyncLoadingTimeoutRef.current = null
      }
    }

    useEffect(() => {
      mountedRef.current = true
      return () => {
        mountedRef.current = false
        clearAsyncLoadingTimeout()
      }
    }, [])

    // If preserveSearchInput is true, we need to control the input value
    const handleInputChange = preserveSearchInput
      ? (newValue: string, actionMeta: {action: string}) => {
          // Only update the input value for certain actions
          if (actionMeta.action === "input-change") {
            setInputValue(newValue)
          } else if (actionMeta.action === "menu-close" && !props.value) {
            // Don't clear the input when the menu closes without a selection
            // unless the component is controlled and has a value
          } else if (actionMeta.action === "set-value") {
            // When an option is selected, clear the input
            setInputValue("")
          }

          // If the original onInputChange was provided, call it too
          if (props.onInputChange) {
            return props.onInputChange(newValue, actionMeta)
          }
          return newValue
        }
      : props.onInputChange

    // Handle blur events if we're preserving input
    const handleBlur = preserveSearchInput
      ? (event: React.FocusEvent<HTMLInputElement>) => {
          // Do not clear input on blur - the value is preserved in our state
          if (props.onBlur) {
            props.onBlur(event)
          }
        }
      : props.onBlur

    // Create a ref to access React Select instance
    const selectRef = useRef<any>(null)
    // True while :set is applying so handleOnChange skips click-only side effects.
    const isProgrammaticSetRef = useRef(false)

    // Mount-time data-default-value for smart Defaults (:clear → read → :set).
    // Only label/value, with try/catch so non-serializable extras never block mount.
    const [dataDefaultValue] = useState(() => {
      const defaultValue = props.defaultValue
      if (defaultValue == null) return undefined
      const options = Array.isArray(defaultValue) ? defaultValue : [defaultValue]
      if (!options.length) return undefined

      try {
        return JSON.stringify(
          options.map((option: SelectValueType) => ({
            label: option?.label,
            value: option?.value,
          })),
        )
      } catch {
        return undefined
      }
    })

    const dialogCtx = useContext(DialogContext)

    const kitContainerRef = useRef<HTMLDivElement>(null)
    const [menuPortalHost, setMenuPortalHost] = useState<HTMLElement | null>(
      null,
    )
    const [floatingOwnerId, setFloatingOwnerId] = useState<string | null>(null)
    const [menuIsOpen, setMenuIsOpen] = useState(false)

    const resolveMenuPortalConfig = useCallback(() => {
      const el = kitContainerRef.current
      setFloatingOwnerId(resolveFloatingOwnerId(el))
      setMenuPortalHost(
        resolvePortaledKitHost(
          el,
          dialogCtx?.selectMenuPortalTarget ?? null,
        ),
      )
    }, [dialogCtx?.selectMenuPortalTarget])

    useLayoutEffect(() => {
      if (!menuIsOpen) {
        setFloatingOwnerId(null)
        setMenuPortalHost(null)
        return
      }
      resolveMenuPortalConfig()
    }, [menuIsOpen, resolveMenuPortalConfig])

    const MenuPortalWithOwner = useMemo(
      () =>
        function TypeaheadMenuPortal(
          portalProps: React.ComponentProps<typeof selectComponents.MenuPortal>,
        ) {
          return (
            <selectComponents.MenuPortal
                {...portalProps}
                innerProps={{
                  ...portalProps.innerProps,
                  ...(floatingOwnerId
                    ? {[PB_FLOATING_OWNER_ATTR]: floatingOwnerId}
                    : {}),
                }}
            />
          )
        },
      [floatingOwnerId],
    )

    const mergedSelectStyles = useMemo(() => {
      if (menuPortalHost !== document.body) return stylesProp
      const zIndex = Number(resolvePortaledFloatingZIndex(document.body))
      return mergeStyles(stylesProp ?? {}, {
        menuPortal: (base: Record<string, unknown>) => ({
          ...base,
          zIndex,
        }),
        menu: (base: Record<string, unknown>) => ({
          ...base,
          zIndex,
        }),
      } as TypeaheadStylesConfig)
    }, [menuPortalHost, stylesProp])

    // Helper function to flatten grouped options if custom groups are used
    const flattenOptions = (options: any[]): any[] => {
      if (!options) return []

      return options.reduce((acc, option) => {
        if (option.options && Array.isArray(option.options)) {
          return [...acc, ...option.options]
        }
        return [...acc, option]
      }, [])
    }

    const handleMenuClose = () => {
      setMenuIsOpen(false)
      setMenuPortalHost(null)
      setFloatingOwnerId(null)
      if (props.onMenuClose) {
        props.onMenuClose()
      }
    }

    // Configure focus on selected option using React Select's API
    const handleMenuOpen = () => {
      if (kitRequiresPortaledFloatingUi(kitContainerRef.current)) {
        resolveMenuPortalConfig()
        setMenuIsOpen(true)
      }
      setTimeout(() => {
        let currentValue = props.value || props.defaultValue

        // Handle react rendered rails version which passes arrays even for single selects
        if (Array.isArray(currentValue) && currentValue.length > 0) {
          currentValue = currentValue[0]
        }

        // Only apply custom focus if user has NOT made a selection yet
        if (
          currentValue &&
          selectRef.current &&
          !hasUserSelected &&
          !props.isMulti
        ) {
          const options = props.options
          if (options) {
            // Flatten grouped options to find the matching option and find matching option
            const flatOptions = flattenOptions(options)

            const targetOption = flatOptions.find((option: any) => {
              const optionValue = props.getOptionValue
                ? props.getOptionValue(option)
                : option.value
              const currentOptionValue = props.getOptionValue
                ? props.getOptionValue(currentValue)
                : currentValue.value
              return optionValue === currentOptionValue
            })

            if (targetOption) {
              // Use React Select's internal state to set focused option
              if (selectRef.current && selectRef.current.setState) {
                selectRef.current.setState({
                  focusedOption: targetOption,
                  focusedValue: null,
                })

                // Handle scrolling so selected option is visible
                setTimeout(() => {
                  if (selectRef.current && selectRef.current.menuListRef) {
                    const menuElement = selectRef.current.menuListRef
                    // Find the focused option using React Select's class
                    const focusedElement = menuElement.querySelector(
                      ".typeahead-kit-select__option--is-focused",
                    )

                    if (focusedElement) {
                      const optionTop = focusedElement.offsetTop
                      const optionHeight = focusedElement.offsetHeight
                      const menuHeight = menuElement.clientHeight

                      // Set the menu's scrollTop to position the selected option in the middle
                      const scrollToMiddle =
                        optionTop - menuHeight / 2 + optionHeight / 2
                      menuElement.scrollTop = Math.max(0, scrollToMiddle)
                    }
                  }
                }, 20)
              }
            }
          }
        }
      }, 0)

      // Call original onMenuOpen if provided
      if (props.onMenuOpen) {
        props.onMenuOpen()
      }
    }

    // Resolve loadOptions (string path to function) once
const resolvedLoadOptions =
  isString(loadOptions) ? get(window, loadOptions) : loadOptions

    // Wrap loadOptions to track async loading safely for BOTH callback + promise styles.
    // Includes watchdog timeout + request sequencing to avoid stuck loading / stale clears.
    const wrappedLoadOptions =
      async
        ? (inputValue: any, callback?: any) => {
            const seq = ++asyncRequestSeqRef.current

            if (mountedRef.current) setAsyncLoading(true)
            clearAsyncLoadingTimeout()

            // Start watchdog for ALL async calls (covers callback + promise + hung requests)
            asyncLoadingTimeoutRef.current = setTimeout(() => {
              if (!mountedRef.current) return
              if (seq !== asyncRequestSeqRef.current) return
              setAsyncLoading(false)
              asyncLoadingTimeoutRef.current = null
            }, 10000)

            const wrappedCallback =
              typeof callback === "function"
                ? (options: any) => {
                    // Only clear if this is still the latest request
                    if (seq === asyncRequestSeqRef.current) {
                      clearAsyncLoadingTimeout()
                      if (mountedRef.current) setAsyncLoading(false)
                    }
                    return callback(options)
                  }
                : undefined

            let result
            try {
              // Preserve signature: if callback is provided, pass it through
              result = wrappedCallback
                ? resolvedLoadOptions(inputValue, wrappedCallback)
                : resolvedLoadOptions(inputValue)
            } catch (e) {
              if (seq === asyncRequestSeqRef.current) {
                clearAsyncLoadingTimeout()
                if (mountedRef.current) setAsyncLoading(false)
              }
              throw e
            }

            // Promise-style completion
            if (result && typeof (result as any).then === "function") {
              return (result as Promise<any>)
                .then((opts) => {
                  if (seq === asyncRequestSeqRef.current) {
                    clearAsyncLoadingTimeout()
                    if (mountedRef.current) setAsyncLoading(false)
                  }
                  return opts
                })
                .catch((e) => {
                  if (seq === asyncRequestSeqRef.current) {
                    clearAsyncLoadingTimeout()
                    if (mountedRef.current) setAsyncLoading(false)
                  }
                  throw e
                })
            }

            // Callback-style will clear via wrappedCallback.
            // Sync/no-callback/no-promise: clear immediately (and cancel watchdog)
            if (!wrappedCallback && seq === asyncRequestSeqRef.current) {
              clearAsyncLoadingTimeout()
              if (mountedRef.current) setAsyncLoading(false)
            }

            return result
          }
        : resolvedLoadOptions

    const selectProps = {
      cacheOptions: true,
      components: {
        Control,
        ClearIndicator,
        IndicatorsContainer,
        IndicatorSeparator: null as null,
        MenuList,
        MultiValue,
        Option,
        Placeholder,
        ValueContainer,
        ...(menuPortalHost ? {MenuPortal: MenuPortalWithOwner} : {}),
        ...components,
      },
      loadOptions: wrappedLoadOptions,
      getOptionLabel: isString(getOptionLabel)
        ? get(window, getOptionLabel)
        : getOptionLabel,
      getOptionValue: isString(getOptionValue)
        ? get(window, getOptionValue)
        : getOptionValue,
      defaultOptions: true,
      id: id || uniqueId(),
      inputDisplay: inputDisplay,
      inline: false,
      isClearable: true,
      isSearchable: true,
      label: label,
      name,
      multiKit: "",
      onCreateOption: null as null,
      plusIcon: false,
      ...(inputDisplay === 'none' ? { hideSelectedOptions: false, closeMenuOnSelect: false } : {}),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onMultiValueClick: (_option: SelectValueType): any => undefined,
      pillColor: pillColor,
      ...(preserveSearchInput ? {inputValue} : {}),
      onInputChange: handleInputChange,
      onBlur: handleBlur,
      required,
      requiredIndicator: requiredIndicator,
      ...props,
      onMenuClose: () => {
        handleMenuClose()
        props.onMenuClose?.()
      },
      onMenuOpen: () => {
        handleMenuOpen()
        props.onMenuOpen?.()
      },
      ...(menuPortalHost
        ? {
            menuPortalTarget: menuPortalHost,
            menuPosition: "fixed" as const,
          }
        : {}),
      styles: mergedSelectStyles,
      isProgrammaticSetRef,
    }

    const [contextValue, setContextValue] = useState("")

    // Track form submit validation for react-rendered Typeahead (own error UI).
    // When inside pb_form_with validate: true, suppress the browser's native
    // invalid UI — PbFormValidation skips this kit so it must not double-show.
    useEffect(() => {
      const handleInvalid = (event: Event) => {
        const target = event.target as HTMLInputElement
        if (!kitContainerRef.current?.contains(target)) return

        // Match by name so we do not have to require ids
        const invalidInputName = target.name || target.getAttribute("name")
        if (invalidInputName !== name) return

        setFormSubmitted(true)

        const validatedForm = target.closest(
          'form[data-pb-form-validation="true"]',
        )
        if (validatedForm) {
          event.preventDefault()
        }
      }
      document.addEventListener("invalid", handleInvalid, true)

      return () => {
        document.removeEventListener("invalid", handleInvalid, true)
      }
    }, [name])

    // Add listener for clearing
    useEffect(() => {
      const handleClear = () => {
        if (preserveSearchInput) {
          setInputValue("")
        }
      }

      document.addEventListener(
        `pb-typeahead-kit-${selectProps.id}:clear`,
        handleClear,
      )

      return () => {
        document.removeEventListener(
          `pb-typeahead-kit-${selectProps.id}:clear`,
          handleClear,
        )
      }
    }, [selectProps.id, preserveSearchInput])

    useEffect(() => {
      if (searchContextSelector) {
        const searchContextElement = document.getElementById(
          searchContextSelector,
        )

        setContextValue((searchContextElement as HTMLInputElement)?.value)
        const handleContextChange = (e: Event) => {
          const target = e.target as HTMLInputElement
          setContextValue(target.value)
          if (clearOnContextChange) {
            document.dispatchEvent(
              new CustomEvent(`pb-typeahead-kit-${selectProps.id}:clear`),
            )
            if (preserveSearchInput) {
              setInputValue("")
            }
          }
        }

        if (searchContextElement)
          searchContextElement.addEventListener("change", handleContextChange)

        return () => {
          if (searchContextElement)
            searchContextElement.removeEventListener(
              "change",
              handleContextChange,
            )
        }
      }
    }, [
      searchContextSelector,
      clearOnContextChange,
      selectProps.id,
      preserveSearchInput,
    ])

    const contextArray = optionsByContext[contextValue]
    if (Array.isArray(contextArray) && contextArray.length > 0) {
      selectProps.options = contextArray
    }

    const Tag = createable
      ? async
        ? AsyncCreateableSelect
        : CreateableSelect
      : async
        ? AsyncSelect
        : Select

    const dispatchReorderEvent = (reorderedValues: SelectValueType[]) => {
      const reorderEvent = new CustomEvent(
        `pb-typeahead-kit-${selectProps.id}-result-option-reorder`,
        {detail: reorderedValues},
      )
      document.dispatchEvent(reorderEvent)
    }

    const handleOnChange = (
      _data: SelectValueType,
      {action, option, removedValue}: TagOnChangeValues,
    ) => {
      const isProgrammaticSet = isProgrammaticSetRef.current

      if (onChange) {
        const isReactHookForm = onChange.toString().includes("target")
        if (isReactHookForm) {
          onChange({target: {name, value: _data}})
        } else {
          onChange(_data)
        }
      }

      // User-selection side effects — skipped for programmatic :set restores
      if (!isProgrammaticSet && (action === "select-option" || action === "create-option")) {
        setFormSubmitted(false)
        // Mark that user has made a selection to disable default value focus behavior
        setHasUserSelected(true)
      }

      // If a value is selected/created and we're preserving input on blur, clear the input
      if (!isProgrammaticSet && (action === "select-option" || action === "create-option") && preserveSearchInput) {
        setInputValue("")
      }

      if (!isProgrammaticSet && (action === "select-option" || action === "create-option")) {
        if (selectProps.onMultiValueClick && option)
          selectProps.onMultiValueClick(option)
        const multiValueClearEvent = new CustomEvent(
          `pb-typeahead-kit-${selectProps.id}-result-option-select`,
          {detail: option ? option : _data},
        )
        document.dispatchEvent(multiValueClearEvent)
      }
      if (action === "remove-value" || action === "pop-value") {
        const multiValueRemoveEvent = new CustomEvent(
          `pb-typeahead-kit-${selectProps.id}-result-option-remove`,
          {detail: removedValue},
        )
        document.dispatchEvent(multiValueRemoveEvent)
      }
      if (action === "clear") {
        const multiValueClearEvent = new CustomEvent(
          `pb-typeahead-kit-${selectProps.id}-result-clear`,
        )
        document.dispatchEvent(multiValueClearEvent)
        // If preserving input on blur, also clear input on explicit clear
        if (preserveSearchInput) {
          setInputValue("")
        }
      }
      if (action === "set-value" && Array.isArray(_data)) {
        dispatchReorderEvent(_data)
      }
    }

    const isPillReorderEnabled = Boolean(
      enablePillReorder && props.isMulti && inputDisplay === 'pills'
    )

    const filteredProps: TypeaheadProps = {...props}
    delete filteredProps.truncate

    const dataProps = buildDataProps(data)
    const htmlProps = buildHtmlProps(htmlOptions)
    const classes = classnames(
      "pb_typeahead_kit react-select",
      `mb_${marginBottom}`,
      globalProps(filteredProps),
      className
    )

    const inlineClass = selectProps.inline ? "inline" : null

    const shouldShowValidationError = required && formSubmitted

    const errorDisplay =
      error ||
      (shouldShowValidationError
        ? validation?.message || "Please fill out this field."
        : "")

    return (
    <div
        ref={kitContainerRef}
        {...dataProps}
        {...htmlProps}
        aria-busy={asyncLoading ? "true" : "false"}
        className={classnames(classes, inlineClass)}
        {...(dataDefaultValue ? { "data-default-value": dataDefaultValue } : {})}
        data-pb-typeahead-loading={asyncLoading ? "true" : "false"}
    >
      <Tag
          {...selectProps}
          classNamePrefix="typeahead-kit-select"
          enablePillReorder={isPillReorderEnabled}
          error={errorDisplay}
          externalDragHandle={externalDragHandle}
          isDisabled={disabled}
          onChange={handleOnChange}
          ref={selectRef}
          showPillIndex={showPillIndex}
      />
    </div>
    )
  },
)

Object.keys(kitComponents).forEach((k) => {
  (Typeahead as GenericObject)[k] = (
    kitComponents as {[key: string]: unknown}
  )[k]
})

Typeahead.displayName = "Typeahead"
export default Typeahead
